import { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { X, Plus } from "lucide-react";
import { SUBCATEGORY_LABELS, SUBCATEGORY_TEMPLATES, getSubcategoryFields } from "../lib/productSubcategories";

/**
 * Renders the subcategory chooser + the dynamic field editor for the chosen subcategory.
 * Props:
 *  - subcategory: current subcategory string (e.g. "clothing")
 *  - attributes: current attributes object
 *  - onSubcategoryChange(newSub)
 *  - onAttributesChange(newAttrs)
 */
export default function ProductSubcategoryFields({
  subcategory,
  attributes,
  onSubcategoryChange,
  onAttributesChange,
}) {
  const [chipInput, setChipInput] = useState({});
  const fields = getSubcategoryFields(subcategory);
  const attrs = attributes || {};

  const setAttr = (key, value) => {
    onAttributesChange({ ...attrs, [key]: value });
  };

  const toggleChip = (key, option) => {
    const cur = Array.isArray(attrs[key]) ? attrs[key] : [];
    const next = cur.includes(option) ? cur.filter((x) => x !== option) : [...cur, option];
    setAttr(key, next);
  };

  const addFreeChip = (key) => {
    const val = (chipInput[key] || "").trim();
    if (!val) return;
    const cur = Array.isArray(attrs[key]) ? attrs[key] : [];
    if (!cur.includes(val)) setAttr(key, [...cur, val]);
    setChipInput({ ...chipInput, [key]: "" });
  };

  const removeFreeChip = (key, val) => {
    const cur = Array.isArray(attrs[key]) ? attrs[key] : [];
    setAttr(key, cur.filter((x) => x !== val));
  };

  return (
    <div className="space-y-4" data-testid="subcategory-fields">
      <div>
        <Label className="text-white">Product subcategory <span className="text-[#6A7280] text-xs font-normal">(optional — pick if you want extra fields)</span></Label>
        <Select
          value={subcategory || ""}
          onValueChange={(v) => {
            onSubcategoryChange(v);
            // NB: We do NOT reset `attributes` here — otherwise the buyer-facing
            // universal Basic Details (colour/size/material/weight/etc.) that the
            // artist already filled in would vanish the moment they pick a
            // subcategory. Subcategory-specific fields just layer on top.
          }}
        >
          <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="subcategory-select">
            <SelectValue placeholder="Choose a subcategory to add detailed fields" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
            {Object.entries(SUBCATEGORY_LABELS).map(([k, label]) => (
              <SelectItem key={k} value={k} className="text-white" data-testid={`subcat-opt-${k}`}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-[#6A7280] mt-1">Pick the closest match — the form below will adapt to that product type.</p>
      </div>

      {subcategory && (
        <div className="border-t border-[#1A2030] pt-4 space-y-4">
          <p className="text-xs uppercase tracking-widest text-[#EC008C] font-bold">
            {SUBCATEGORY_LABELS[subcategory]} details
          </p>
          {fields.map((f) => {
            const val = attrs[f.key];
            const tid = `attr-${f.key}`;
            if (f.type === "text") {
              return (
                <div key={f.key}>
                  <Label className="text-white">{f.label}</Label>
                  <Input
                    value={val || ""}
                    onChange={(e) => setAttr(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    data-testid={tid}
                  />
                </div>
              );
            }
            if (f.type === "textarea") {
              return (
                <div key={f.key}>
                  <Label className="text-white">{f.label}</Label>
                  <Textarea
                    value={val || ""}
                    onChange={(e) => setAttr(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    rows={3}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    data-testid={tid}
                  />
                </div>
              );
            }
            if (f.type === "number") {
              return (
                <div key={f.key}>
                  <Label className="text-white">{f.label}</Label>
                  <Input
                    type="number"
                    value={val ?? ""}
                    onChange={(e) => setAttr(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    data-testid={tid}
                  />
                </div>
              );
            }
            if (f.type === "date") {
              return (
                <div key={f.key}>
                  <Label className="text-white">{f.label}</Label>
                  <Input
                    type="date"
                    value={val || ""}
                    onChange={(e) => setAttr(f.key, e.target.value)}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    data-testid={tid}
                  />
                </div>
              );
            }
            if (f.type === "toggle") {
              return (
                <label key={f.key} className="flex items-center gap-2 cursor-pointer" data-testid={tid}>
                  <input
                    type="checkbox"
                    checked={!!val}
                    onChange={(e) => setAttr(f.key, e.target.checked)}
                    className="w-4 h-4 accent-[#EC008C]"
                  />
                  <span className="text-sm text-white">{f.label}</span>
                </label>
              );
            }
            if (f.type === "select") {
              return (
                <div key={f.key}>
                  <Label className="text-white">{f.label}</Label>
                  <Select value={val || ""} onValueChange={(v) => setAttr(f.key, v)}>
                    <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid={tid}>
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o} className="text-white">
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            if (f.type === "multi-select" || f.type === "chips") {
              const selected = Array.isArray(val) ? val : [];
              return (
                <div key={f.key}>
                  <Label className="text-white">{f.label}</Label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid={tid}>
                    {f.options.map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => toggleChip(f.key, o)}
                        className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                          selected.includes(o)
                            ? "bg-[#EC008C] text-black border-[#EC008C]"
                            : "bg-transparent text-[#8A92A0] border-[#1A2030] hover:border-[#EC008C]"
                        }`}
                        data-testid={`${tid}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }
            if (f.type === "chips-free") {
              const selected = Array.isArray(val) ? val : [];
              return (
                <div key={f.key}>
                  <Label className="text-white">{f.label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={chipInput[f.key] || ""}
                      onChange={(e) => setChipInput({ ...chipInput, [f.key]: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addFreeChip(f.key);
                        }
                      }}
                      placeholder={f.placeholder || "Type and press Enter"}
                      className="bg-[#050810] border-[#1A2030] text-white text-sm"
                      data-testid={tid}
                    />
                    <button
                      type="button"
                      onClick={() => addFreeChip(f.key)}
                      className="p-2 rounded-md bg-[#EC008C] text-black hover:bg-[#FF3399]"
                      data-testid={`${tid}-add-btn`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  {selected.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selected.map((s) => (
                        <span
                          key={s}
                          className="px-2 py-1 rounded-full text-xs bg-[#EC008C]/15 text-[#EC008C] border border-[#EC008C]/40 flex items-center gap-1"
                        >
                          {s}
                          <button
                            type="button"
                            onClick={() => removeFreeChip(f.key, s)}
                            className="ml-1 hover:text-white"
                            data-testid={`${tid}-remove-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

// Static exports so other consumers can inspect the templates
export { SUBCATEGORY_LABELS, SUBCATEGORY_TEMPLATES };
